import React, { useState, useEffect, useCallback } from 'react';
import { CVIProvider } from './components/cvi/components/cvi-provider';
import TavusConversation from './components/TavusConversation';
import SMSControl from './components/SMSControl';
import SMSQuestions from './components/SMSQuestions';
import ConversationMonitor from './components/ConversationMonitor';
import './App.css';

function App() {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ending, setEnding] = useState(false);
  const [sendTextFunction, setSendTextFunction] = useState(null);

  // Debug logging for sendTextFunction changes
  useEffect(() => {
    console.log('🔄 sendTextFunction changed:', typeof sendTextFunction, sendTextFunction);
    if (typeof sendTextFunction === 'object' && sendTextFunction && typeof sendTextFunction.then === 'function') {
      console.error('❌ CRITICAL: sendTextFunction is a Promise! This means the function was called instead of being set.');
    }
  }, [sendTextFunction]);

  // Stable callback to prevent infinite re-renders
  const handleSendTextMessage = useCallback((fn) => {
    console.log('🔄 App received function:', typeof fn, fn);
    
    // Check if it's a Promise (which means the function was called)
    if (typeof fn === 'object' && fn && typeof fn.then === 'function') {
      console.error('❌ CRITICAL: Received a Promise instead of function! The function was called accidentally.');
      return; // Don't set a Promise as the function
    }
    
    // Only set if it's actually a function or null
    if (fn === null || typeof fn === 'function') {
      // Use React's functional state update to prevent the function from being called
      setSendTextFunction(prevState => fn);
    } else {
      console.error('❌ Attempted to set non-function as sendTextFunction:', typeof fn, fn);
    }
  }, []);

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      console.log('🔍 DEBUG - Environment Check:', {
        REACT_APP_API_URL: process.env.REACT_APP_API_URL,
        REACT_APP_ENVIRONMENT: process.env.REACT_APP_ENVIRONMENT,
        NODE_ENV: process.env.NODE_ENV,
        resolvedApiUrl: apiUrl,
        fullEndpoint: `${apiUrl}/api/conversations`
      });
      
      const response = await fetch(`${apiUrl}/api/conversations`);
      const data = await response.json();
      if (data.success) {
        setConversations(data.conversations);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };

  const createNewConversation = async (sessionName) => {
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      console.log('🚀 DEBUG - Creating conversation with endpoint:', `${apiUrl}/api/conversations`);
      
      const response = await fetch(`${apiUrl}/api/conversations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ sessionName })
      });

      const data = await response.json();
      if (data.success) {
        setActiveConversation(data.conversation);
        await loadConversations();
      } else {
        alert('Error creating conversation: ' + data.error);
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      alert('Error creating conversation: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const endCurrentConversation = async () => {
    if (!activeConversation) {
      return;
    }

    setEnding(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/api/conversations/${activeConversation.id}/end`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        console.log('Session ended successfully');
        setActiveConversation(null);
        setSendTextFunction(null);
        await loadConversations();
      } else {
        console.error('Failed to end session');
        alert('Error ending session. Please try again.');
      }
    } catch (error) {
      console.error('Error ending session:', error);
      alert('Error ending session: ' + error.message);
    } finally {
      setEnding(false);
    }
  };

  const sendSMSTrigger = async (text, from = 'React App') => {
    if (!activeConversation) {
      alert('Please select an active conversation first');
      return;
    }

    console.log('🔍 Debug - sendTextFunction type:', typeof sendTextFunction);
    console.log('🔍 Debug - sendTextFunction value:', sendTextFunction);
    console.log('🔍 Debug - sendTextFunction toString:', sendTextFunction.toString());

    if (!sendTextFunction) {
      alert('Daily.co connection not ready. Please wait for the conversation to connect.');
      return;
    }

    // sendTextFunction should now be the actual function (not a wrapper)
    if (typeof sendTextFunction !== 'function') {
      console.error('❌ sendTextFunction is not a function, it is:', typeof sendTextFunction);
      alert('Error: sendTextFunction is not a function. Please wait for connection.');
      return;
    }

    try {
      console.log(`📱 Sending SMS trigger: "${text}" from ${from}`);
      
      // Use the frontend Daily.co connection to send the text
      const success = await sendTextFunction(text);
      
      if (success) {
        console.log('✅ SMS sent to avatar via Daily.co');
        return { success: true, message: `Text sent to avatar: "${text}"` };
      } else {
        throw new Error('Failed to send text via Daily.co');
      }
    } catch (error) {
      console.error('Error sending SMS:', error);
      alert('Error sending SMS: ' + error.message);
      throw error;
    }
  };

  const handleEndConversation = useCallback((conversationId) => {
    console.log(`🔚 Conversation ${conversationId} ended, refreshing conversation list`);
    
    // Clear active conversation if it was the one that ended
    if (activeConversation && activeConversation.id === conversationId) {
      setActiveConversation(null);
      setSendTextFunction(null);
    }
    
    // Refresh the conversation list to update status
    loadConversations();
  }, [activeConversation, loadConversations]);

  return (
    <CVIProvider>
      <div className="App">
      <header className="app-header">
        <h1>Presidio Total Experience</h1>
      </header>

      <main className="app-main">
        <div className="app-grid">
          {/* Left Panel - Controls */}
          <div className="control-panel">
            <div className="create-conversation">
              <h3>{activeConversation ? 'End Session' : 'Create New Session'}</h3>
              <button 
                onClick={activeConversation ? endCurrentConversation : () => createNewConversation('SMS Demo ' + Date.now())}
                disabled={loading || ending}
                className={activeConversation ? 'end-btn' : 'create-btn'}
              >
                {activeConversation 
                  ? (ending ? 'Ending Session...' : 'End Session')
                  : (loading ? 'Creating Session...' : 'Create New Session')
                }
              </button>
            </div>

            {/*<SMSControl 
              onSendSMS={sendSMSTrigger}
              activeConversation={activeConversation}
            />*/}

            <SMSQuestions 
              activeConversation={activeConversation}
            />

            {/*<ConversationMonitor 
              conversations={conversations}
              activeConversation={activeConversation}
              onSelectConversation={setActiveConversation}
              onRefresh={loadConversations}
            />*/}
          </div>

          {/* Right Panel - Video */}
          <div className="video-panel">
            <TavusConversation 
              conversation={activeConversation}
              onSendTextMessage={handleSendTextMessage}
              onEnd={handleEndConversation}
            />
          </div>
        </div>
      </main>
      </div>
    </CVIProvider>
  );
}

export default App;